<template>
  <OverlayScrollbarsComponent
    v-if="overlayScrollbarsApplied"
    class="overlayscrollbars-vue h-full min-h-0 w-full"
    ref="osRef"
    :style="{ display: elementHidden ? 'none' : undefined }"
    :options="mergedOptions"
    :events="mergedEvents"
    :defer="defer"
  >
    <div class="h-full min-h-0">
      <slot></slot>
    </div>
  </OverlayScrollbarsComponent>
</template>


<script setup lang="js">
import {
  OverlayScrollbarsComponent,
  useOverlayScrollbars,
} from "overlayscrollbars-vue";
import "overlayscrollbars/styles/overlayscrollbars.css";
import { computed, onMounted, ref } from "vue";
import { useEventObserver } from "../../composables/useEventObserver";

const props = defineProps({
  direction: {
    type: String,
    default: "vertical", // 'vertical' (default) or 'horizontal'
  },
  options: {
    type: Object,
    default: () => ({}),
  },
  events: {
    type: Object,
    default: () => ({}),
  },
  defer: {
    type: Boolean,
    default: true,
  },
});

const elementHidden = ref(false);
const overlayScrollbarsApplied = ref(true);
const bodyOverlayScrollbarsApplied = ref(null);
const [, activateEvent] = useEventObserver();
const [initBodyOverlayScrollbars] = useOverlayScrollbars({
  defer: true,
  events: {
    initialized: () => {
      bodyOverlayScrollbarsApplied.value = true;
    },
    destroyed: () => {
      bodyOverlayScrollbarsApplied.value = false;
    },
  },
  options: {
    scrollbars: {
      theme: "os-theme-light",
      clickScroll: true,
      autoHide: "leave",
      autoHideDelay: 150,
    },
  },
});

onMounted(() => initBodyOverlayScrollbars(document.body));

const mergedOptions = computed(() => {
  const baseScrollbars = {
    theme: "os-theme-light",
    clickScroll: true,
    autoHide: "leave",
    autoHideDelay: 0,
  };

  const baseOverflow =
    props.direction === "horizontal"
      ? { x: "scroll", y: "hidden" }
      : { x: "hidden", y: "scroll" };

  return {
    ...props.options,
    overflow: {
      ...baseOverflow,
      ...(props.options.overflow || {}),
    },
    scrollbars: {
      ...baseScrollbars,
      ...(props.options.scrollbars || {}),
    },
  };
});

const mergedEvents = computed(() => {
  const user = props.events || {};
  const wrap = (name) => {
    const userHandler = user[name];
    return (...args) => {
      activateEvent(name);
      if (typeof userHandler === "function") {
        userHandler(...args);
      }
    };
  };

  return {
    ...user,
    initialized: wrap("initialized"),
    destroyed: wrap("destroyed"),
    updated: wrap("updated"),
    scroll: wrap("scroll"),
  };
});
</script>
